import levenshtein from 'fast-levenshtein';

// Matching between a Check Sources segment (what the model quoted from the
// transcript) and a text node of the source document shown in the Files panel.
//
// Comparison form: whitespace removed, punctuation/symbols removed, lower-cased.
// Unicode property escapes keep CJK text intact — the previous
// /[^\w\s]/ stripped every Chinese character, leaving '', which every string
// "includes": a Chinese selection lit up the whole document, and an English
// selection over a Chinese transcript could never match anything.
// Removing (rather than collapsing) whitespace also lets a quote match an ASR
// transcript that has spaces between every word ("菌藥 就 好像").
export const normalizeForMatch = (str) =>
  String(str ?? '').replace(/\s+/g, '').replace(/[\p{P}\p{S}]/gu, '').toLowerCase();

const MIN_MATCH_LEN = 4;      // shorter than this matches everywhere
const FUZZY_THRESHOLD = 0.55; // legacy Levenshtein similarity fallback

const collapse = (str) => String(str ?? '').replace(/\s+/g, ' ').trim();

// A document sentence matches a segment when either contains the other, or as a
// fallback when they are similar enough as whole strings (near-identical lines
// with transcription differences).
export const sentenceMatchesSegment = (sentence, segment) => {
  const a = normalizeForMatch(sentence);
  const b = normalizeForMatch(segment);
  if (a.length < MIN_MATCH_LEN || b.length < MIN_MATCH_LEN) return false;
  if (a.includes(b) || b.includes(a)) return true;
  const sa = collapse(sentence);
  const sb = collapse(segment);
  const maxLength = Math.max(sa.length, sb.length);
  if (!maxLength) return false;
  const distance = levenshtein.get(sa, sb, { useCollator: true });
  return 1 - distance / maxLength >= FUZZY_THRESHOLD;
};

// True when a document text node should be highlighted as a source for the
// current selection: some returned segment matches it, or it literally contains
// the selected report text.
export const isSourceSentence = (sentence, segments, selectedText) => {
  if (!collapse(sentence)) return false;
  const selected = normalizeForMatch(selectedText);
  if (selected.length >= MIN_MATCH_LEN && normalizeForMatch(sentence).includes(selected)) return true;
  return (segments || []).some(
    (segment) => typeof segment === 'string' && sentenceMatchesSegment(sentence, segment)
  );
};
